import React from 'react';
import { Link } from 'react-router-dom';
import WishlistButton from './WishlistButton';
import API_BASE_URL from '../global/apiConfig';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { FiShoppingCart } from 'react-icons/fi';
import { BiSolidStar, BiSolidStarHalf, BiStar } from 'react-icons/bi';
import compressImage from '../utils/compressImage';
import { UserRole } from '../config/userRole';

const ProductCard = ({ product, wishlist, setWishlist, token, onQuickAdd, addingId }) => {
  const getPackInfo = () => {
    const variation = product?.product_variations?.find(
      (v) => v?.pack_price != null && v?.pack_qty != null
    );
    const packPrice = variation?.pack_price ?? product?.pack_price;
    const packQty = variation?.pack_qty ?? product?.pack_qty;
    return { packPrice, packQty };
  };

  const productRatings = Array.isArray(product?.product_ratings) ? product.product_ratings : [];
  const totalRatings = productRatings.length;
  const overallRating = totalRatings > 0
    ? productRatings.reduce((sum, item) => sum + item.rating, 0) / totalRatings
    : 0;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow relative h-full">
      <div className="absolute z-10 top-2 left-2 flex flex-col gap-1 items-start">
        {Number(product.is_sale) === 1 && (
          <span className="px-2 py-0.5 rounded-md bg-red-500 text-white text-[10px] font-semibold uppercase">
            Sale
          </span>
        )}
        {product.product_tag && product.product_tag !== 'null' && (
          <span className="px-2 py-0.5 rounded-md bg-brand-600 text-white text-[10px] font-semibold uppercase">
            {product.product_tag}
          </span>
        )}
      </div>

      <div className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/90 shadow-sm flex items-center justify-center">
        <WishlistButton
          product={product}
          wishlist={wishlist}
          setWishlist={setWishlist}
          token={token}
          API_BASE_URL={API_BASE_URL}
        />
      </div>

      <Link to={`/p/${product.slug}`} className="relative block aspect-[4/5] bg-gray-50">
        <LazyLoadImage
          src={compressImage(product.primary_img, 400, 70, 'webp')}
          alt={product.prod_name}
          effect="blur"
          width="100%"
          height="100%"
          className="w-full h-full object-cover"
          wrapperClassName="w-full h-full block"
        />
      </Link>

      {UserRole.userRole !== 'distributor' && (
        <button
          type="button"
          onClick={() => onQuickAdd?.(product)}
          disabled={addingId === product.id}
          className="hidden md:flex absolute bottom-[102px] right-2 z-10 w-8 h-8 rounded-full bg-brand-600 hover:bg-brand-700 shadow-sm items-center justify-center text-white disabled:opacity-60"
          aria-label="Add to cart"
        >
          <FiShoppingCart size={14} />
        </button>
      )}

      <div className="p-3 md:p-4">
        {product.brand_details?.brand_name && (
          <h3 className="text-[10px] md:text-xs text-gray-400 font-semibold uppercase tracking-wide truncate">
            {product.brand_details.brand_name}
          </h3>
        )}
        <Link
          to={`/p/${product.slug}`}
          className="block text-xs md:text-sm text-gray-900 truncate font-semibold mt-0.5"
          title={product.prod_name}
        >
          {product.prod_name}
        </Link>

        {totalRatings > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <div className="flex items-center text-yellow-500 text-[11px]">
              {Array.from({ length: Math.floor(overallRating) }, (_, i) => (
                <BiSolidStar key={`f-${i}`} />
              ))}
              {overallRating % 1 !== 0 && <BiSolidStarHalf />}
              {Array.from({ length: 5 - Math.ceil(overallRating) }, (_, i) => (
                <BiStar key={`e-${i}`} className="text-gray-300" />
              ))}
            </div>
            <span className="text-[10px] text-gray-500">({totalRatings})</span>
          </div>
        )}

        {UserRole.userRole === 'distributor' ? (
          (() => {
            const { packPrice, packQty } = getPackInfo();
            if (!packPrice || !packQty) return null;
            return (
              <div className="flex flex-col mt-1.5 text-[10px] md:text-xs text-gray-500">
                <div className="font-bold text-gray-900 text-sm">₹{packPrice}</div>
                <div>Pack of {packQty}</div>
              </div>
            );
          })()
        ) : (
          <div className="flex gap-1.5 md:gap-2 items-end mt-1.5 flex-wrap">
            <p className="text-gray-900 text-xs md:text-sm font-bold">
              ₹{product.sale_price}
            </p>
            {product.regular_price > product.sale_price && (
              <p className="text-gray-400 text-[11px] line-through md:text-sm">
                ₹{product.regular_price}
              </p>
            )}
            {product.regular_price > product.sale_price && (
              <p className="text-green-600 font-bold text-[10px] md:text-xs">
                {(
                  ((product.regular_price - product.sale_price) / product.regular_price) *
                  100
                ).toFixed(0)}% OFF
              </p>
            )}
          </div>
        )}

        {(product.product_tag || product.product_quality) && (
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {(product.product_tag && product.product_tag !== 'null') && (
              <span className="px-2 py-0.5 rounded-md bg-green-100 text-green-700 font-semibold uppercase text-[9px] md:text-[10px]">
                {product.product_tag}
              </span>
            )}
            {(product.product_quality && product.product_quality !== 'null') && (
              <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 font-semibold uppercase text-[9px] md:text-[10px]">
                {product.product_quality}
              </span>
            )}
          </div>
        )}

        {UserRole.userRole !== 'distributor' && (
          <button
            type="button"
            onClick={() => onQuickAdd?.(product)}
            disabled={addingId === product.id}
            className="md:hidden mt-3 w-full flex items-center justify-center gap-1.5 border border-brand-600 text-brand-600 hover:bg-brand-50 font-semibold text-xs py-2 rounded-lg transition-colors disabled:opacity-60"
          >
            <FiShoppingCart size={13} />
            {addingId === product.id ? 'Adding...' : 'Add to Cart'}
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
